import { INITIAL_PAGE } from '@protector/utils/constants';
import type { EntityAdapter } from 'redux-ngrx-entity';
import { createEntityAdapter } from 'redux-ngrx-entity';
import * as CompanyActions from './company.actions';
import type { Company, CompanyState, LicensingStatusResponse, AvailableWorkspace } from './company.models';

const { CompanyActionsTypes } = CompanyActions;

type CompanyActionsFunctions = Omit<typeof CompanyActions, 'CompanyActionsTypes'>;
type ActionsResponses = ReturnType<CompanyActionsFunctions[keyof CompanyActionsFunctions]>;

function selectCompanyUuid(company: Company): string {
  return company.id;
}

function sortByName(a: Company, b: Company): number {
  return a.name.localeCompare(b.name);
}

const entity: EntityAdapter<Company> = createEntityAdapter<Company>({
  selectId: selectCompanyUuid,
  sortComparer: sortByName
});

export const initialState = entity.getInitialState<CompanyState>({
  ids: [],
  entities: {},
  page: INITIAL_PAGE,
  isLoading: false,
  isLoaded: false,
  error: null,
  licensingStatus: null,
  isLoadingLicensingStatus: true,
  currentWorkspaceId: null,
  isLoadingCurrentWorkspaceId: false,
  availableWorkspaces: []
});

interface IMutableContent {
  mutableContent: Company[];
}

export default (state = initialState, action: ActionsResponses) => {
  switch (action.type) {
    case CompanyActionsTypes.LOAD_COMPANIES:
      return {
        ...state,
        isLoading: true,
        isLoaded: false
      };
    case CompanyActionsTypes.LOAD_COMPANIES_SUCCESS:
      return entity.addAll((action.payload as IMutableContent).mutableContent, {
        ...state,
        page: action.payload,
        isLoading: false,
        isLoaded: true,
        error: null
      });
    case CompanyActionsTypes.LOAD_COMPANIES_FAILURE:
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
        error: action.payload
      };

    case CompanyActionsTypes.LOAD_CURRENT_WORKSPACE_ID:
      return {
        ...state,
        isLoadingCurrentWorkspaceId: true
      };

    case CompanyActionsTypes.LOAD_CURRENT_WORKSPACE_ID_SUCCESS:
    case CompanyActionsTypes.SET_CURRENT_WORKSPACE_ID:
      return {
        ...state,
        isLoadingCurrentWorkspaceId: false,
        currentWorkspaceId: action.payload as string
      };

    case CompanyActionsTypes.LOAD_CURRENT_WORKSPACE_ID_FAILURE:
      return {
        ...state,
        isLoadingCurrentWorkspaceId: false,
        error: action.payload as string
      };

    case CompanyActionsTypes.LOAD_LICENSING_STATUS:
    case CompanyActionsTypes.LOAD_LICENSING_STATUS_BY_WORKSPACE:
      return {
        ...state,
        isLoadingLicensingStatus: true
      };

    case CompanyActionsTypes.LOAD_LICENSING_STATUS_SUCCESS:
      return {
        ...state,
        licensingStatus: action.payload as LicensingStatusResponse,
        isLoadingLicensingStatus: false,
        error: null
      };
    case CompanyActionsTypes.CLEAN_LICENSING_STATUS:
      return {
        ...state,
        licensingStatus: null,
        isLoadingLicensingStatus: false,
        error: null
      };
    case CompanyActionsTypes.LOAD_LICENSING_STATUS_FAILURE:
      return {
        ...state,
        isLoadingLicensingStatus: false
      };
    case CompanyActionsTypes.SET_AVAILABLE_WORKSPACES:
      return {
        ...state,
        availableWorkspaces: action.payload as AvailableWorkspace[]
      };
    default:
      return state;
  }
};

const { selectEntities, selectAll } = entity.getSelectors();

export const selectCompanyEntities = selectEntities;
export const selectAllCompanies = selectAll;
